import React from 'react';
import { ModalX } from '../../../components';
import { Button, Input, Option, Select, Spinner } from '@material-tailwind/react';
import { useAddUser, useUpdateUser } from '../../../custom-hooks/use-users';
import { convertToPresentableText, NotifyError } from '../../../utils/functions';
import { STATIC_USER_ROLES } from '../../../utils/enums';
import { useGetRoles } from '../../../custom-hooks/use-roles';



// Functional component
const UserFormModal = ({
    isOpen,
    onClose,
    isUpdating,
    updatingUserId,
    defaultData,

}) => {
    // 
    const addUserMutation = useAddUser();
    const updateUserMutation = useUpdateUser();
    const { data: roles = [], isLoading: rolesLoading } = useGetRoles();

    // Resolve the dynamic role ID from the defaultData — it may be an object or a string
    const defaultDynamicRoleId = defaultData.dynamicRole?._id || defaultData.dynamicRole || '';

    // State variables
    const [formData, setFormData] = React.useState({
        ...defaultData,
        userType: 'staff',
        role: STATIC_USER_ROLES.STAFF,
        dynamicRole: defaultDynamicRoleId,
    });

    // Re-sync the dynamicRole once the roles list has loaded.
    // On first open, roles=[] so the Select has no options and can't match the value.
    // This effect runs when roles arrive and force-sets the correct value.
    React.useEffect(() => {
        if (roles.length > 0 && defaultDynamicRoleId) {
            setFormData(prev => ({ ...prev, dynamicRole: defaultDynamicRoleId }));
        }
    }, [roles]);

    // Handle form input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };



    // Handle form submission
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                email: formData.email,
                name: formData.name,
                cnic: formData.cnic,
                phone: formData.phone,
            };

            payload.role = STATIC_USER_ROLES.STAFF;
            payload.dynamicRole = formData.dynamicRole || null;

            if (isUpdating) {
                await updateUserMutation.mutateAsync({
                    userId: updatingUserId,
                    data: payload
                });
            } else {
                payload.password = formData.password;
                await addUserMutation.mutateAsync(payload);
            }
            onClose(false);
            setFormData(defaultData);
        } catch (error) {
            console.error(error);
            NotifyError(error.response.data.message, error);
        }
    };
    return (
        <ModalX
            isOpen={isOpen}
            onClose={() => onClose(false)}
            title={isUpdating ? "Update User" : "Add User"}
            maxWidth="md"
        >
            <form onSubmit={handleFormSubmit}>
                <div className="space-y-4">
                    <Input
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        label="CNIC"
                        name="cnic"
                        value={formData.cnic}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                    />
                    {!isUpdating && (
                        <Input
                            label="Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                        />
                    )}
                    <Select
                        label={rolesLoading ? "Loading roles..." : "Select Role (Permissions)"}
                        value={formData.dynamicRole || undefined}
                        onChange={(value) => handleSelectChange("dynamicRole", value)}
                        disabled={rolesLoading}
                    >
                        {roles.map((role) => (
                            <Option key={role._id} value={role._id}>
                                {role.name}
                                {role.isSystemRole ? ' (System)' : ''}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                    <Button
                        onClick={() => onClose(false)}
                        className="rounded-full"
                        variant="text"
                    >
                        Cancel
                    </Button>
                    <Button disabled={updateUserMutation.isLoading} type="submit" className="rounded-full flex gap-2">
                        {updateUserMutation.isLoading && <Spinner className='w-4 h-4' />}{isUpdating ? "Update" : "Add"}
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default UserFormModal;